import { useMemo } from "react"
import BarChart from "~/ui-rtk/components/ui/charts/BarChart/BarChart"
import ChartLoader from "~/ui-rtk/components/ui/charts/ChartLoader"
import { Card } from "~/ui-rtk/components/ui/common"
import { H4 } from "~/ui-rtk/components/ui/typography"
import {
  VISUALIZATIONS_MAP,
  VISUALIZATION_TYPE,
} from "~/ui-rtk/constants/charts"
import useSources from "~/ui-rtk/hooks/sources"
import { CHANNEL_GROUP_LABEL } from "~/ui-rtk/constants/brand-value-overview"
import { useValidatedUrlState } from "~/ui-rtk/hooks/useValidatedUrlState"
import DropdownMenu from "~/ui-rtk/components/ui/controls/DropdownMenu/DropdownMenu"
import AnimatedContainer from "~/ui-rtk/components/ui/controls/AnimatedButton/AnimatedButton"
import { ChevronDownIcon } from "@heroicons/react/24/outline"

export const WIDGET = "brand_value_by_payout_curve_chart"

/**
 * Valid channel group values that can be used for filtering
 * Prevents URL parameter injection by validating against this whitelist
 */
const VALID_CHANNEL_GROUPS = [
  "all",
  "ad",
  "organic",
  "mentions",
  "subscription",
  "search",
  "unattributed",
] as const

type ChannelGroup = (typeof VALID_CHANNEL_GROUPS)[number]

const CHANNEL_GROUP_OPTIONS = [
  { value: "all", label: "All Channel Groups" },
  { value: "ad", label: CHANNEL_GROUP_LABEL.ad },
  { value: "organic", label: CHANNEL_GROUP_LABEL.organic },
  { value: "mentions", label: CHANNEL_GROUP_LABEL.mentions },
  { value: "subscription", label: CHANNEL_GROUP_LABEL.subscription },
  { value: "search", label: CHANNEL_GROUP_LABEL.search },
  { value: "unattributed", label: CHANNEL_GROUP_LABEL.unattributed },
]

/**
 * Props for the BrandValueByPayoutCurve component
 */
export interface BrandValueByPayoutCurveProps {
  /**
   * Date range for the main query period
   */
  dateRange: [Date, Date]
  /**
   * Optional Cube.js query parameters to filter data
   * These are merged with internal channel group filters
   */
  cubeQueryParams?: Record<string, string | string[]>
  /**
   * Optional comparison date range for period-over-period analysis
   */
  compareRange?: [Date, Date]
}

export default function BrandValueByPayoutCurve({
  dateRange,
  cubeQueryParams,
  compareRange,
}: BrandValueByPayoutCurveProps) {
  const visualization = VISUALIZATIONS_MAP[WIDGET]
  const { sources } = useSources()

  // Use validated URL state to prevent injection attacks
  const { state: selectedChannelGroup, setState: setSelectedChannelGroup } =
    useValidatedUrlState("bvpc_channel_group", VALID_CHANNEL_GROUPS, "all")

  const channelGroupFilter = useMemo(() => {
    if (selectedChannelGroup === "all") return {}

    return {
      "company_platform_brand_value.channel_group": [selectedChannelGroup],
    }
  }, [selectedChannelGroup])

  const handleChannelGroupChange = (value: string | number) => {
    // Validate that the value is a valid channel group before setting
    if (VALID_CHANNEL_GROUPS.includes(value as ChannelGroup)) {
      setSelectedChannelGroup(value as ChannelGroup)
    }
  }

  const selectedLabel = useMemo(
    () =>
      CHANNEL_GROUP_OPTIONS.find(
        option => option.value === selectedChannelGroup,
      )?.label ?? "All Channel Groups",
    [selectedChannelGroup],
  )

  return (
    <Card rounded className="bg-system-blue-800 h-full">
      <div className="flex justify-between items-center mb-system-xs">
        <H4>Brand Value Payout Curve</H4>
        <div className="flex-shrink-0">
          <DropdownMenu
            className="relative inline-block p-0"
            options={CHANNEL_GROUP_OPTIONS}
            onItemClick={handleChannelGroupChange}
          >
            <AnimatedContainer>
              <span className="flex items-center gap-2 text-sm">
                {selectedLabel}
                <ChevronDownIcon className="h-4 w-4" />
              </span>
            </AnimatedContainer>
          </DropdownMenu>
        </div>
      </div>
      <ChartLoader
        chartId={WIDGET}
        className="border-0 h-[calc(100%-3rem)]"
        widgetType={VISUALIZATION_TYPE.BarChart}
        component={BarChart}
        dateRange={dateRange}
        compareRange={compareRange}
        sources={sources}
        hideAggSelector={true}
        requiredSources={visualization.requiredSources}
        cubeQueryParams={{
          [WIDGET]: {
            ...cubeQueryParams,
            ...channelGroupFilter,
          },
        }}
        description={visualization.description ?? undefined}
        compareMode={false}
        {...visualization.props}
      />
    </Card>
  )
}
